import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, FloppyDisk, Check } from "@phosphor-icons/react";
import { API, authHeaders } from "@/lib/api";
import { useLocale } from "@/i18n";
import ImportProfile from "./ImportProfile";
import TeamTab from "./TeamTab";

export default function ProfileTab({ barber, onSaved }) {
  const { t } = useLocale();
  const WEEKDAYS = t("weekdays_full");
  const [form, setForm] = useState({
    shop_name: barber.shop_name || "",
    description: barber.description || "",
    whatsapp: barber.whatsapp || "",
    address: barber.address || "",
    city: barber.city || "",
  });
  const [schedule, setSchedule] = useState(() => ({ ...(barber.schedule || {}) }));
  const [banner, setBanner] = useState(barber.banner || 1);
  const [depositEnabled, setDepositEnabled] = useState(!!barber.deposit_enabled);
  const [depositAmount, setDepositAmount] = useState(barber.deposit_amount != null ? String(barber.deposit_amount) : "");
  const [depositKey, setDepositKey] = useState(barber.deposit_pix_key || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleDay = (wd) => {
    setSchedule((s) => {
      const next = { ...s };
      if (next[String(wd)]) delete next[String(wd)];
      else next[String(wd)] = { open: "09:00", close: "19:00" };
      return next;
    });
  };

  const setDayTime = (wd, field, value) => {
    setSchedule((s) => ({ ...s, [String(wd)]: { ...s[String(wd)], [field]: value } }));
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/barber/photo`, fd, { headers: authHeaders() });
      toast.success(t("photo_ok"));
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("photo_error"));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/barber/profile`, {
        ...form,
        schedule,
        banner,
        deposit_enabled: depositEnabled,
        deposit_amount: depositAmount === "" ? null : Number(depositAmount.replace(",", ".")),
        deposit_pix_key: depositKey,
      }, { headers: authHeaders() });
      toast.success(t("prof_saved"));
      onSaved();
    } catch {
      toast.error(t("prof_error"));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "mt-1.5 w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors";

  return (
    <>
    <form onSubmit={handleSave} className="space-y-6" data-testid="profile-tab">
      <div className="flex items-center gap-4">
        {barber.photo_url ? (
          <img src={barber.photo_url} alt="Foto" className="w-20 h-20 rounded-full object-cover border-2 border-amber-500" data-testid="profile-photo-preview" />
        ) : (
          <div className="w-20 h-20 rounded-full border-2 border-zinc-700 bg-zinc-800 flex items-center justify-center" data-testid="profile-photo-empty">
            <Camera size={24} className="text-zinc-500" weight="duotone" />
          </div>
        )}
        <label className="cursor-pointer">
          <input data-testid="photo-upload-input" type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
          <span className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-zinc-800 text-sm font-semibold text-zinc-300 hover:border-amber-500/60 hover:text-amber-500 transition-colors">
            <Camera size={16} weight="duotone" />
            {uploading ? t("uploading") : t("prof_photo_change")}
          </span>
        </label>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-semibold text-zinc-300">{t("prof_shop")}</label>
          <input data-testid="profile-shop-input" type="text" value={form.shop_name} onChange={set("shop_name")} className={inputCls} />
        </div>
        <div>
          <label className="text-sm font-semibold text-zinc-300">{t("prof_desc")}</label>
          <textarea
            data-testid="profile-description-input"
            value={form.description}
            onChange={set("description")}
            rows={3}
            placeholder={t("prof_desc_ph")}
            className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors resize-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold text-zinc-300">WhatsApp</label>
            <input data-testid="profile-whatsapp-input" type="tel" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} />
          </div>
          <div>
            <label className="text-sm font-semibold text-zinc-300">{t("prof_city")}</label>
            <input data-testid="profile-city-input" type="text" value={form.city} onChange={set("city")} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold text-zinc-300">{t("prof_address")}</label>
          <input data-testid="profile-address-input" type="text" value={form.address} onChange={set("address")} placeholder={t("prof_address_ph")} className={inputCls} />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-zinc-300 mb-3">{t("prof_banner")}</h3>
        <div className="grid grid-cols-3 gap-2" data-testid="banner-picker">
          {[1, 2, 3].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`banner-option-${n}`}
              onClick={() => setBanner(n)}
              className={`relative rounded-lg overflow-hidden border-2 transition-colors duration-200 ${
                banner === n ? "border-amber-500" : "border-zinc-800 hover:border-zinc-600"
              }`}
            >
              <img src={`/banner-${n}.webp`} alt={`Banner ${n}`} className="w-full h-14 object-cover" />
              {banner === n && (
                <span className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                  <Check size={20} weight="bold" className="text-amber-400 drop-shadow" />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-zinc-300 mb-3">{t("prof_hours")}</h3>
        <div className="space-y-2" data-testid="schedule-editor">
          {WEEKDAYS.map((label, wd) => {
            const cfg = schedule[String(wd)];
            return (
              <div key={wd} className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5" data-testid={`schedule-row-${wd}`}>
                <label className="flex items-center gap-2 w-28 shrink-0 cursor-pointer">
                  <input
                    data-testid={`schedule-toggle-${wd}`}
                    type="checkbox"
                    checked={!!cfg}
                    onChange={() => toggleDay(wd)}
                    className="h-4 w-4 accent-amber-500"
                  />
                  <span className="text-sm text-zinc-300">{label}</span>
                </label>
                {cfg ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      data-testid={`schedule-open-${wd}`}
                      type="time"
                      value={cfg.open}
                      onChange={(e) => setDayTime(wd, "open", e.target.value)}
                      className="flex-1 h-9 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm focus:border-amber-500 focus:outline-none"
                    />
                    <span className="text-zinc-600 text-xs">{t("prof_to")}</span>
                    <input
                      data-testid={`schedule-close-${wd}`}
                      type="time"
                      value={cfg.close}
                      onChange={(e) => setDayTime(wd, "close", e.target.value)}
                      className="flex-1 h-9 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm focus:border-amber-500 focus:outline-none"
                    />
                  </div>
                ) : (
                  <span className="text-xs text-zinc-600">{t("closed")}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3" data-testid="deposit-section">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-zinc-300">{t("deposit_title")}</h3>
            <p className="text-xs text-zinc-500 mt-0.5">{t("deposit_hint")}</p>
          </div>
          <button
            type="button"
            data-testid="deposit-toggle"
            onClick={() => setDepositEnabled(!depositEnabled)}
            className={`shrink-0 w-11 h-6 rounded-full transition-colors relative ${depositEnabled ? "bg-amber-500" : "bg-zinc-700"}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-zinc-50 transition-all ${depositEnabled ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
        {depositEnabled && (
          <div className="grid grid-cols-2 gap-3">
            <input
              data-testid="deposit-amount-input"
              type="text"
              inputMode="decimal"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              placeholder={t("deposit_amount_ph")}
              className="w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors"
            />
            <input
              data-testid="deposit-key-input"
              type="text"
              value={depositKey}
              onChange={(e) => setDepositKey(e.target.value)}
              placeholder={t("deposit_key_ph")}
              className="w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors"
            />
          </div>
        )}
      </div>

      <button
        data-testid="profile-save-btn"
        type="submit"
        disabled={saving}
        className="w-full h-12 rounded-lg bg-amber-500 text-zinc-950 font-bold flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
      >
        <FloppyDisk size={18} weight="bold" />
        {saving ? t("prof_saving") : t("prof_save")}
      </button>

      {!barber?.imported && <ImportProfile onImported={onSaved} />}
    </form>

    <div className="mt-10 pt-6 border-t border-zinc-800">
      <TeamTab />
    </div>
    </>
  );
}
